import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuotaCheck } from '../hooks/useQuotaCheck';

/**
 * QuotaCounter component displays remaining quota for operations, storage, and training
 * Shows real-time usage statistics in the navigation bar
 * Supports both monthly and hourly (Stundenkontingent) plan types
 */
const QuotaCounter: React.FC = () => {
  const { t } = useTranslation('pricing');
  const { subscription, usage, checking } = useQuotaCheck();

  if (checking || !subscription || !usage) {
    return null;
  }

  // Calculate storage usage (shared across plan types)
  const storageLimit = subscription.max_storage_gb || 0;
  const storageUsed = usage.storage_used_gb || 0;
  const storagePercentage = storageLimit > 0 ? (storageUsed / storageLimit) * 100 : 0;

  // Determine color based on percentage
  const getColorClass = (percentage: number): string => {
    if (percentage >= 90) return 'text-red-600 dark:text-red-400';
    if (percentage >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getBarColorClass = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Format expiration date (a timestamptz instant) in local time.
  const formatExpirationDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Format a calendar date ('YYYY-MM-DD', e.g. next_reset_date) WITHOUT timezone
  // conversion — new Date('YYYY-MM-DD') parses as UTC midnight, so local getDate()
  // would be off by one for users west of UTC. Read the parts directly.
  const formatCalendarDate = (dateString: string): string => {
    const [y, m, d] = dateString.slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  };

  const daysUntilExpiry = Math.ceil(
    (new Date(subscription.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  const expiryColorClass = daysUntilExpiry <= 7
    ? 'text-red-600 dark:text-red-400'
    : daysUntilExpiry <= 14
    ? 'text-yellow-600 dark:text-yellow-400'
    : 'text-green-600 dark:text-green-400';

  // Shared expiration display — used by both hourly and monthly branches
  const ExpirationInfo = () => {
    return (
      <div className="text-center space-y-1 overflow-hidden">
        <span className="text-xs font-medium uppercase tracking-wide block truncate text-foreground dark:text-gray-300">
          {subscription.plan_name}
        </span>
        <div className="text-xs text-muted-foreground dark:text-gray-400 truncate">
          <span className="whitespace-nowrap">
            {t('quota.expiresOn')}: {formatExpirationDate(subscription.expires_at)}
          </span>
          <span className={`ml-1 font-medium whitespace-nowrap ${expiryColorClass}`}>
            ({daysUntilExpiry} {t('quota.days')})
          </span>
        </div>
        {usage?.next_reset_date && (
          <div className="text-xs text-muted-foreground dark:text-gray-400 truncate">
            <span className="whitespace-nowrap">
              {t('quota.resetsOn')}: {formatCalendarDate(usage.next_reset_date)}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Hourly plan: compute hours display
  // Activate compute-hours UI for any plan that publishes a positive
  // compute-hours allowance (BASIC/STANDARD/PREMIUM today; Free, API ONLY,
  // and future count-only plans fall through to the monthly counters).
  const usesComputeHours =
    subscription.total_compute_hours !== null &&
    subscription.total_compute_hours !== undefined &&
    subscription.total_compute_hours > 0;
  if (usesComputeHours) {
    const totalHours = subscription.total_compute_hours!;
    const usedSeconds = usage.total_compute_seconds ?? 0;
    const totalMinutes = totalHours * 60;
    const usedMinutes = usedSeconds / 60;
    const computePercentage = totalMinutes > 0 ? (usedMinutes / totalMinutes) * 100 : 0;
    const computeDisplay = `${usedMinutes.toFixed(0)}min/${totalMinutes.toFixed(0)}min`;

    return (
      <div className="px-3 py-3 space-y-3 border-t border-border dark:border-gray-700">
        <ExpirationInfo />

        {/* Compute Hours Counter (Rechenstunden) */}
        <div className="space-y-1">
          <div className="flex items-center justify-between min-w-0 gap-2">
            <span className="text-xs text-muted-foreground dark:text-gray-400 truncate">
              {t('quota.computeHours', { defaultValue: 'Rechenstunden' })}
            </span>
            <span className={`text-xs font-semibold whitespace-nowrap ${getColorClass(computePercentage)}`}>
              {computeDisplay}
            </span>
          </div>
          <div className="w-full bg-muted dark:bg-gray-700 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${getBarColorClass(computePercentage)}`}
              style={{ width: `${Math.min(100, computePercentage)}%` }}
            />
          </div>
        </div>

        {/* Storage Counter */}
        <div className="space-y-1">
          <div className="flex items-center justify-between min-w-0 gap-2">
            <span className="text-xs text-muted-foreground dark:text-gray-400 truncate">
              {t('quota.storage')}
            </span>
            <span className={`text-xs font-semibold whitespace-nowrap ${getColorClass(storagePercentage)}`}>
              {storageUsed.toFixed(2)}/{storageLimit} GB
            </span>
          </div>
          <div className="w-full bg-muted dark:bg-gray-700 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${getBarColorClass(storagePercentage)}`}
              style={{ width: `${Math.min(100, storagePercentage)}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Monthly plan: original display
  const processingLimit = subscription.max_processing_jobs_per_month || 0;
  const processingUsed = usage.processing_jobs_count || 0;
  const processingPercentage = processingLimit > 0 ? (processingUsed / processingLimit) * 100 : 0;

  const trainingLimit = subscription.max_training_runs_per_month || 0;
  const trainingUsed = usage.training_runs_count || 0;
  const isTrainingUnlimited = subscription.max_training_runs_per_month === -1;
  const trainingPercentage = !isTrainingUnlimited && trainingLimit > 0 ? (trainingUsed / trainingLimit) * 100 : 0;

  return (
    <div className="px-3 py-3 space-y-3 border-t border-border dark:border-gray-700">
      <ExpirationInfo />

      {/* Processing Counter (Verarbeitung) */}
      <div className="space-y-1">
        <div className="flex items-center justify-between min-w-0 gap-2">
          <span className="text-xs text-muted-foreground dark:text-gray-400 truncate">
            {t('quota.processing', { defaultValue: 'Processing' })}
          </span>
          <span className={`text-xs font-semibold whitespace-nowrap ${getColorClass(processingPercentage)}`}>
            {processingUsed}/{processingLimit}
          </span>
        </div>
        <div className="w-full bg-muted dark:bg-gray-700 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${getBarColorClass(processingPercentage)}`}
            style={{ width: `${Math.min(100, processingPercentage)}%` }}
          />
        </div>
      </div>

      {/* Training Counter */}
      <div className="space-y-1">
        <div className="flex items-center justify-between min-w-0 gap-2">
          <span className="text-xs text-muted-foreground dark:text-gray-400 truncate">
            {t('quota.training', { defaultValue: 'Training' })}
          </span>
          <span className={`text-xs font-semibold whitespace-nowrap ${isTrainingUnlimited ? 'text-green-600 dark:text-green-400' : getColorClass(trainingPercentage)}`}>
            {trainingUsed}/{isTrainingUnlimited ? '\u221E' : trainingLimit}
          </span>
        </div>
        <div className="w-full bg-muted dark:bg-gray-700 rounded-full h-1.5">
          {isTrainingUnlimited ? (
            <div className="h-1.5 rounded-full bg-green-500" style={{ width: '100%' }} />
          ) : (
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${getBarColorClass(trainingPercentage)}`}
              style={{ width: `${Math.min(100, trainingPercentage)}%` }}
            />
          )}
        </div>
      </div>

      {/* Storage Counter */}
      <div className="space-y-1">
        <div className="flex items-center justify-between min-w-0 gap-2">
          <span className="text-xs text-muted-foreground dark:text-gray-400 truncate">
            {t('quota.storage')}
          </span>
          <span className={`text-xs font-semibold whitespace-nowrap ${getColorClass(storagePercentage)}`}>
            {storageUsed.toFixed(2)}/{storageLimit} GB
          </span>
        </div>
        <div className="w-full bg-muted dark:bg-gray-700 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${getBarColorClass(storagePercentage)}`}
            style={{ width: `${Math.min(100, storagePercentage)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default QuotaCounter;
